/**
 * SignaturePad.jsx - Digital signature component for timesheet signing
 * Allows employees to draw their signature with finger/mouse
 */

import { useRef, useState, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { PenTool, RotateCcw, Check, AlertTriangle, Shield, Calendar, Monitor, Clock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get device info for legal purposes
const getDeviceInfo = () => {
  const nav = navigator;
  return {
    userAgent: nav.userAgent,
    platform: nav.platform || "unknown",
    language: nav.language,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    colorDepth: window.screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    touchEnabled: 'ontouchstart' in window || nav.maxTouchPoints > 0,
    cookiesEnabled: nav.cookieEnabled,
    onlineStatus: nav.onLine,
    deviceMemory: nav.deviceMemory || "unknown",
    hardwareConcurrency: nav.hardwareConcurrency || "unknown",
    connectionType: nav.connection?.effectiveType || "unknown",
    timestamp: new Date().toISOString()
  };
};

// Get month name in Romanian
const getMonthName = (month) => {
  const months = [
    "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
    "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"
  ];
  return months[month - 1] || "";
};

export default function SignaturePad({ 
  open, 
  onOpenChange, 
  userId, 
  userName, 
  companyId,
  month,
  year,
  onSignatureComplete 
}) {
  const sigCanvas = useRef(null);
  const [loading, setLoading] = useState(false);
  const [signatureStatus, setSignatureStatus] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);

  useEffect(() => {
    if (open && userId && month && year) {
      fetchSignatureStatus();
      setDeviceInfo(getDeviceInfo());
      setAcceptedTerms(false);
    }
  }, [open, userId, month, year]);

  const fetchSignatureStatus = async () => {
    try {
      const response = await axios.get(`${API}/signatures/status/${userId}/${month}/${year}`);
      setSignatureStatus(response.data);
    } catch (error) {
      console.error("Error fetching signature status:", error);
    }
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const handleSign = async () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      toast.error("Te rog să desenezi semnătura");
      return;
    }

    if (!acceptedTerms) {
      toast.error("Trebuie să accepți declarația pentru a semna");
      return;
    }

    // Check if user can still sign (has less than 2 signatures)
    if (signatureStatus && !signatureStatus.can_sign) {
      toast.error("Ai atins limita maximă de 2 semnături pentru această lună");
      return;
    }

    setLoading(true);
    try {
      // Get signature canvas directly - using getCanvas() for better compatibility
      const canvas = sigCanvas.current.getCanvas();
      
      // Create a white background canvas (signatures need white bg)
      const bgCanvas = document.createElement('canvas');
      bgCanvas.width = canvas.width;
      bgCanvas.height = canvas.height;
      const ctx = bgCanvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, bgCanvas.width, bgCanvas.height);
      ctx.drawImage(canvas, 0, 0);
      
      // Convert to PNG
      const signatureImage = bgCanvas.toDataURL("image/png");
      
      const response = await axios.post(`${API}/signatures/sign`, {
        user_id: userId,
        user_name: userName,
        company_id: companyId,
        month: month,
        year: year,
        signature_image: signatureImage,
        device_info: deviceInfo
      }, {
        timeout: 30000 // 30 second timeout for slow connections
      });

      toast.success(`Pontajul pentru ${getMonthName(month)} ${year} a fost semnat cu succes!`);
      
      if (onSignatureComplete) {
        onSignatureComplete(response.data);
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error("Signature error:", error);
      let errorMsg = "Eroare la semnare";
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(d => d.msg || JSON.stringify(d)).join(', ');
        } else {
          errorMsg = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = `Eroare de conexiune: ${error.message}`;
      }
      
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const canSign = !signatureStatus || signatureStatus.can_sign;
  const signatureCount = signatureStatus?.signature_count || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[95vh] flex flex-col" data-testid="signature-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <PenTool className="w-5 h-5 text-teal-600" />
            Semnează Pontajul
          </DialogTitle>
          <DialogDescription>
            {getMonthName(month)} {year}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {/* Status Badge */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium">Luna: {getMonthName(month)} {year}</span>
            </div>
            {signatureCount > 0 ? (
              <Badge className={signatureCount >= 2 ? "bg-green-600" : "bg-yellow-500"}>
                Semnat {signatureCount}/2
              </Badge>
            ) : (
              <Badge variant="outline" className="border-orange-300 text-orange-600">
                Nesemnat
              </Badge>
            )}
          </div>

          {/* Previous signatures info */}
          {signatureStatus?.signatures?.length > 0 && (
            <Card className="border-slate-200 bg-slate-50">
              <CardContent className="p-3">
                <p className="text-xs font-semibold text-slate-600 mb-2">Semnături anterioare:</p>
                {signatureStatus.signatures.map((sig, idx) => (
                  <div key={sig.id} className="flex items-center justify-between text-xs py-1 border-b border-slate-200 last:border-0">
                    <span className="text-slate-600">Semnătura {sig.signature_count}</span>
                    <span className="text-slate-500">
                      {new Date(sig.signed_at).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {!canSign ? (
            /* Max signatures reached */
            <Alert className="border-green-300 bg-green-50">
              <Check className="h-5 w-5 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Pontajul este complet semnat.</strong><br/>
                Ai atins numărul maxim de 2 semnături pentru această lună.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Legal notice */}
              <Alert className="border-orange-300 bg-orange-50">
                <Shield className="h-5 w-5 text-orange-600" />
                <AlertDescription className="text-sm text-orange-800">
                  <strong>Notă juridică:</strong> Semnătura digitală are valoare juridică conform 
                  Regulamentului eIDAS și Legii 455/2001. Se vor înregistra: data, ora, 
                  dispozitivul și adresa IP pentru autenticitate.
                </AlertDescription>
              </Alert>

              {/* Device info preview */}
              {deviceInfo && (
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-3 space-y-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Monitor className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-semibold text-blue-800">Informații înregistrate:</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-xs text-blue-700">
                      <span>Dispozitiv: {deviceInfo.platform}</span>
                      <span>Ecran: {deviceInfo.screenWidth}x{deviceInfo.screenHeight}</span>
                      <span>Fus orar: {deviceInfo.timezone}</span>
                      <span>Touch: {deviceInfo.touchEnabled ? "Da" : "Nu"}</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Signature Canvas */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Desenează semnătura:</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSignature}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Șterge
                  </Button>
                </div>
                <div 
                  className="border-2 border-dashed border-slate-300 rounded-lg bg-white"
                  style={{ touchAction: 'none' }}
                >
                  <SignatureCanvas
                    ref={sigCanvas}
                    canvasProps={{
                      width: 400,
                      height: 150,
                      className: "signature-canvas rounded-lg",
                      style: { width: '100%', height: '150px' }
                    }}
                    backgroundColor="white"
                    penColor="black"
                  />
                </div>
                <p className="text-xs text-slate-500 text-center">
                  Folosește mouse-ul sau degetul pentru a desena semnătura
                </p>
              </div>

              {/* Terms acceptance */}
              <div className="flex items-start space-x-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <Checkbox
                  id="accept-terms"
                  checked={acceptedTerms}
                  onCheckedChange={setAcceptedTerms}
                  data-testid="accept-terms-checkbox"
                />
                <Label htmlFor="accept-terms" className="text-xs leading-relaxed cursor-pointer">
                  Declar că am verificat și confirm exactitatea datelor din pontajul pentru 
                  luna <strong>{getMonthName(month)} {year}</strong>. Înțeleg că această semnătură 
                  electronică are valoare juridică și va fi înregistrată împreună cu data, ora 
                  și informațiile dispozitivului.
                </Label>
              </div>
            </>
          )}
        </div>

        {canSign && (
          <DialogFooter className="flex-shrink-0 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="mr-2"
            >
              Anulează
            </Button>
            <Button
              onClick={handleSign}
              disabled={loading || !acceptedTerms}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="confirm-signature-button"
            >
              {loading ? (
                "Se procesează..."
              ) : (
                <>
                  <PenTool className="w-4 h-4 mr-2" />
                  Semnează Pontajul ({signatureCount + 1}/2)
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
